import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import '../styles/UploadProgressIndicator.css';

interface UploadItem {
    fileId: string;
    fileName: string;
    status: 'uploading' | 'queued' | 'processing' | 'success' | 'error';
    error?: string;
    sopTitle?: string;
}

interface MultiUploadProgressIndicatorProps {
    uploads: UploadItem[];
    onClose: (fileId: string) => void;
}

export const MultiUploadProgressIndicator: React.FC<MultiUploadProgressIndicatorProps> = ({
    uploads,
    onClose
}) => {
    const [isMinimized, setIsMinimized] = useState(false);
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

    // Filter out completed/uploads that should be hidden
    const activeUploads = uploads.filter(upload => 
        upload.status !== 'success' || expandedItems.has(upload.fileId)
    );

    if (activeUploads.length === 0) return null;

    const toggleItemExpansion = (fileId: string) => {
        setExpandedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fileId)) {
                newSet.delete(fileId);
            } else {
                newSet.add(fileId);
            }
            return newSet;
        });
    };

    const getStatusText = (status: UploadItem['status'], error?: string) => {
        switch (status) {
            case 'uploading':
                return 'Uploading file...';
            case 'queued':
                return 'Waiting in queue...';
            case 'processing':
                return 'Processing PDF (this may take a few minutes)';
            case 'success':
                return 'Document uploaded and processed successfully';
            case 'error':
                return error || 'An error occurred during upload';
            default:
                return '';
        }
    };

    const getStatusColor = (status: UploadItem['status']) => {
        switch (status) {
            case 'success':
                return 'success';
            case 'error':
                return 'error';
            default:
                return 'processing';
        }
    };

    return (
        <div className={`upload-progress-indicator ${isMinimized ? 'minimized' : ''}`}>
            <div className="upload-progress-header">
                <div className="upload-progress-title">
                    <span>Uploading Progress ({activeUploads.length} {activeUploads.length === 1 ? 'file' : 'files'})</span>
                </div>
                <div className="upload-progress-actions">
                    <button
                        className="upload-progress-btn"
                        onClick={() => setIsMinimized(!isMinimized)}
                        aria-label={isMinimized ? 'Expand' : 'Minimize'}
                    >
                        {isMinimized ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                </div>
            </div>

            {!isMinimized && (
                <div className="upload-progress-body">
                    <div className="multi-upload-list">
                        {activeUploads.map((upload) => {
                            const isExpanded = expandedItems.has(upload.fileId);
                            return (
                                <div key={upload.fileId} className={`upload-item status-${getStatusColor(upload.status)}`}>
                                    <div className="upload-item-header">
                                        <div className="upload-item-info d-flex align-items-center gap-10">
                                            <div className="upload-progress-icon">
                                                {(upload.status === 'uploading' || upload.status === 'processing' || upload.status === 'queued') && (
                                                    <div className="upload-spinner">
                                                        <div className="spinner-ring"></div>
                                                    </div>
                                                )}
                                                {upload.status === 'success' && (
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <circle cx="12" cy="12" r="10" fill="#10B981" />
                                                        <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                                {upload.status === 'error' && (
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <circle cx="12" cy="12" r="10" fill="#EF4444" />
                                                        <path d="M12 8V12M12 16H12.01" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                                    </svg>
                                                )}
                                            </div>
                                            <div>
                                            <div className="upload-sop-title" title={upload.sopTitle}>SOP: {upload.sopTitle}</div>
                                            <div className="upload-status-text">{getStatusText(upload.status, upload.error)}</div>
                                            </div>
                                            {/* <div className="upload-file-name">{upload.fileName}</div> */}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiUploadProgressIndicator;

